import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Tabs,
  Form,
  Input,
  Select,
  DatePicker,
  Checkbox,
  Button,
  Table,
  Modal,
  InputNumber,
  notification,
  Typography,
  Spin,
  Tag,
  Space,
} from 'antd';
import { PlusOutlined, ThunderboltOutlined, CheckOutlined } from '@ant-design/icons';
import moment from 'moment';

import request from '../../lib/request';
import { AttachmentsPanel, AttachmentsButton, useAttachmentCounts } from '../../components/AttachmentsPanel';
import ProposalEditorModal, { proposalColumns } from '../../components/ProposalEditorModal';
import ClientBooksCard from '../../components/ClientBooksCard';
import ClientWorkbookTab from '../../components/ClientWorkbookTab';

import { date as fmtDate, label } from '../../lib/format';
import { CountrySelect } from '../../components/CountrySelect';
const CLIENT_TYPES = ['COMPANY', 'INDIVIDUAL', 'PARTNERSHIP'];
const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH'];

const ENTITY_TYPES = ['COMPANY', 'INDIVIDUAL', 'PARTNERSHIP', 'TRUST', 'OTHER'];

/** Entity picker used by every entity-level register; defaults to the client's primary entity. */
function EntitySelect({ entities }) {
  return (
    <Form.Item name="entity_id" label="Entity" tooltip="Which of the client's legal entities this belongs to">
      <Select
        options={entities.map((e) => ({ value: e.id, label: e.is_primary ? `${e.entity_name} (primary)` : e.entity_name }))}
      />
    </Form.Item>
  );
}

function entityColumn(entities) {
  const byId = Object.fromEntries(entities.map((e) => [e.id, e]));
  return { title: 'Entity', dataIndex: 'entity_id', render: (v) => byId[v]?.entity_name || '-' };
}

/** Generic "list + add via modal" tab body, reused across the Client's nested resources. */
function NestedResourceTab({ clientId, endpoint, columns, fields, onSaved, rows, entities, attachmentsOwnerType }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [form] = Form.useForm();
  const primaryEntity = (entities || []).find((e) => e.is_primary) || (entities || [])[0];
  // Rows that carry files (KYC documents, document requests) get a Files column with the count.
  const { counts, refresh: refreshCounts } = useAttachmentCounts(attachmentsOwnerType || 'CLIENT', attachmentsOwnerType ? (rows || []).map((r) => r.id) : []);

  const handleSave = () => {
    form.validateFields().then((values) => {
      request
        .post(`/clients/${clientId}/${endpoint}`, values)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Saved' });
            setModalOpen(false);
            onSaved();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const allColumns = entities ? [entityColumn(entities), ...columns] : [...columns];
  if (attachmentsOwnerType) {
    allColumns.push({
      title: 'Files',
      key: 'files',
      width: 110,
      render: (_, row) => (
        <AttachmentsButton
          ownerType={attachmentsOwnerType}
          ownerId={row.id}
          count={counts[row.id]}
          title={`Files - ${row.document_type || row.category || ''}`.replace(/_/g, ' ')}
          onChange={() => {
            refreshCounts();
            onSaved(); // an upload can change the row itself (a document request becomes FULFILLED)
          }}
        />
      ),
    });
  }

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            if (primaryEntity) form.setFieldsValue({ entity_id: primaryEntity.id });
            setModalOpen(true);
          }}
        >
          Add
        </Button>
      </div>
      <Table rowKey="id" size="small" columns={allColumns} dataSource={rows} pagination={false} />
      <Modal title="Add" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSave}>
        <Form form={form} layout="vertical">
          {entities && <EntitySelect entities={entities} />}
          {fields}
        </Form>
      </Modal>
    </div>
  );
}

/**
 * Entities tab: the client's registered legal entities (spec Part III). The
 * ROC / Tax & SI facts live here, one entity is primary and is what officers,
 * KYC documents, engagements and invoices default to.
 */
function EntitiesTab({ clientId, entities, onSaved }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ entity_type: 'COMPANY', status: 'ACTIVE' });
    setModalOpen(true);
  };

  const openEdit = (entity) => {
    setEditing(entity);
    form.setFieldsValue({ ...entity, incorporation_date: entity.incorporation_date ? moment(entity.incorporation_date) : null });
    setModalOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload = { ...values, incorporation_date: values.incorporation_date ? values.incorporation_date.format('YYYY-MM-DD') : null };
      const call = editing
        ? request.put(`/clients/${clientId}/entities/${editing.id}`, payload)
        : request.post(`/clients/${clientId}/entities`, payload);
      call
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: editing ? 'Entity updated' : 'Entity added' });
            setModalOpen(false);
            onSaved();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={openCreate}>
          Add Entity
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={entities}
        columns={[
          { title: 'Entity', dataIndex: 'entity_name', render: (v, e) => (e.is_primary ? <span>{v} <Tag color="blue">primary</Tag></span> : v) },
          { title: 'Type', dataIndex: 'entity_type' },
          { title: 'Registration No.', dataIndex: 'registration_number' },
          { title: 'Incorporated', dataIndex: 'incorporation_date', render: (v) => fmtDate(v) },
          { title: 'Country', dataIndex: 'country' },
          { title: 'Tax ID / VAT', dataIndex: 'tax_id' },
          { title: 'SI No.', dataIndex: 'si_number' },
          { title: 'Tax Residency', dataIndex: 'tax_residency' },
          { title: 'Non-Dom', dataIndex: 'non_dom', render: (v) => (v ? 'Yes' : '') },
          { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>{label(v)}</Tag> },
          { title: '', key: 'actions', render: (_, e) => <Button size="small" onClick={() => openEdit(e)}>Edit</Button> },
        ]}
      />
      <Modal title={editing ? `Edit ${editing.entity_name}` : 'New Entity'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSave}>
        <Form form={form} layout="vertical">
          <Form.Item name="entity_name" label="Entity Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="entity_type" label="Type" rules={[{ required: true }]}>
            <Select options={ENTITY_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Typography.Title level={5}>ROC</Typography.Title>
          <Form.Item name="registration_number" label="Registration Number">
            <Input />
          </Form.Item>
          <Form.Item name="incorporation_date" label="Incorporation Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="country" label="Country of Registration">
            <CountrySelect />
          </Form.Item>
          <Typography.Title level={5}>Tax & SI</Typography.Title>
          <Form.Item name="tax_id" label="Tax ID / VAT Number">
            <Input />
          </Form.Item>
          <Form.Item name="si_number" label="SI Number">
            <Input />
          </Form.Item>
          <Form.Item name="tax_residency" label="Tax Residency">
            <Input />
          </Form.Item>
          <Form.Item name="non_dom" valuePropName="checked">
            <Checkbox>Non-Domiciled</Checkbox>
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={['ACTIVE', 'INACTIVE'].map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="is_primary" valuePropName="checked" tooltip="Registers and invoices default to the primary entity">
            <Checkbox>Primary entity</Checkbox>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

/**
 * Service Engagements tab: same "add via modal" pattern as the other nested
 * resources, plus an Activate action (generates the Project/Tasks/Deadline
 * via lib/workflow_engine.js on the backend) and an expandable Task list
 * once activated.
 */
function EngagementsTab({ clientId, rows, onSaved, entities }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [projects, setProjects] = React.useState({});
  const [activating, setActivating] = React.useState(null);
  const [form] = Form.useForm();

  const handleSave = () => {
    form.validateFields().then((values) => {
      request
        .post(`/clients/${clientId}/service-engagements`, {
          ...values,
          entity_id: values.entity_id || (entities || []).find((e) => e.is_primary)?.id || null,
          start_date: values.start_date.format('YYYY-MM-DD'),
        })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Saved' });
            setModalOpen(false);
            onSaved();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const handleActivate = (engagement) => {
    setActivating(engagement.id);
    request
      .post(`/client-service-engagements/${engagement.id}/activate`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `Activated - ${r.data.tasksCreated} task(s) created` });
          onSaved();
        } else {
          notification.error({ message: r.message });
        }
      })
      .finally(() => setActivating(null));
  };

  const loadProjectTasks = (projectId) => {
    if (projects[projectId]) return;
    request
      .get(`/projects/${projectId}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) setProjects((prev) => ({ ...prev, [projectId]: r.data }));
      });
  };

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Add
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        dataSource={rows}
        columns={[
          entityColumn(entities || []),
          { title: 'Service', dataIndex: 'service_id' },
          { title: 'Frequency', dataIndex: 'frequency' },
          { title: 'Start Date', dataIndex: 'start_date', render: (v) => fmtDate(v) },
          { title: 'Status', dataIndex: 'status' },
          {
            title: '',
            key: 'actions',
            render: (_, engagement) =>
              engagement.project_id ? (
                <Tag color="green">Activated (Project #{engagement.project_id})</Tag>
              ) : (
                <Button
                  size="small"
                  icon={<ThunderboltOutlined />}
                  loading={activating === engagement.id}
                  onClick={() => handleActivate(engagement)}
                >
                  Activate
                </Button>
              ),
          },
        ]}
        expandable={{
          expandedRowRender: (engagement) => {
            if (!engagement.project_id) return <Typography.Text type="secondary">Not activated yet.</Typography.Text>;
            const project = projects[engagement.project_id];
            if (!project) return <Spin size="small" />;
            return (
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={project.Tasks || []}
                columns={[
                  { title: 'Task', dataIndex: 'subject' },
                  { title: 'Start', dataIndex: 'exp_start_date', render: (v) => fmtDate(v) },
                  { title: 'End', dataIndex: 'exp_end_date', render: (v) => fmtDate(v) },
                  { title: 'Status', dataIndex: 'status', render: (v) => <Tag>{label(v)}</Tag> },
                ]}
              />
            );
          },
          onExpand: (expanded, engagement) => {
            if (expanded && engagement.project_id) loadProjectTasks(engagement.project_id);
          },
        }}
      />

      <Modal title="New Service Engagement" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSave}>
        <Form form={form} layout="vertical">
          {entities && <EntitySelect entities={entities} />}
          <Form.Item name="service_id" label="Service ID" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="frequency" label="Frequency" rules={[{ required: true }]}>
            <Select options={['ONE_OFF', 'MONTHLY', 'QUARTERLY', 'ANNUALLY'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="costing_company_id" label="Costing Company ID" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

const DEADLINE_STATUS_COLORS = { PENDING: 'blue', FILED: 'green', OVERDUE: 'red' };

/** Deadlines tab: this client's own compliance deadlines (auto-generated by engagement activation, or added manually). */
function DeadlinesTab({ clientId, rows, onSaved }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [form] = Form.useForm();

  const handleCreate = () => {
    form.validateFields().then((values) => {
      request
        .post('/deadlines', { ...values, client_id: clientId, due_date: values.due_date.format('YYYY-MM-DD') })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Deadline created' });
            setModalOpen(false);
            onSaved();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const handleMarkFiled = (deadline) => {
    request
      .put(`/deadlines/${deadline.id}`, { status: 'FILED', filed_date: moment().format('YYYY-MM-DD') })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: 'Marked as filed' });
          onSaved();
        } else {
          notification.error({ message: r.message });
        }
      });
  };

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Add
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        dataSource={rows}
        columns={[
          { title: 'Due Date', dataIndex: 'due_date', render: (v) => fmtDate(v) },
          { title: 'Type', dataIndex: 'deadline_type' },
          { title: 'Period', dataIndex: 'period' },
          { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={DEADLINE_STATUS_COLORS[v]}>{label(v)}</Tag> },
          { title: 'Notes', dataIndex: 'notes', ellipsis: true },
          {
            title: '',
            key: 'actions',
            render: (_, d) =>
              d.status !== 'FILED' && (
                <Button size="small" icon={<CheckOutlined />} onClick={() => handleMarkFiled(d)}>
                  Mark Filed
                </Button>
              ),
          },
        ]}
      />

      <Modal title="New Deadline" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate}>
        <Form form={form} layout="vertical">
          <Form.Item name="deadline_type" label="Type" initialValue="OTHER">
            <Select options={['VAT', 'VIES', 'OSS', 'IOSS', 'OTHER'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="period" label="Period">
            <Input placeholder="e.g. Q3 2026" />
          </Form.Item>
          <Form.Item name="due_date" label="Due Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

const LETTER_STATUS_COLORS = { DRAFT: 'default', SENT: 'blue', SIGNED: 'green', DECLINED: 'red', CANCELLED: 'red', SUPERSEDED: 'orange' };

/** Engagement Letters tab: this client's letters + one-click draft generation from its accepted proposals. */
function LettersTab({ clientId, entities }) {
  const [rows, setRows] = React.useState(null);
  const [generating, setGenerating] = React.useState(false);

  const fetchLetters = () => {
    request
      .get('/engagement-letters', { client_id: clientId })
      .then((r) => r.json())
      .then((r) => r.status && setRows(r.data));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchLetters, [clientId]);

  const handleGenerate = () => {
    setGenerating(true);
    const primary = (entities || []).find((e) => e.is_primary);
    request
      .post('/engagement-letters/generate', { client_id: Number(clientId), entity_id: primary ? primary.id : null })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `Draft ${r.data.letter_number} generated` });
          fetchLetters();
        } else {
          notification.error({ message: r.message });
        }
      })
      .finally(() => setGenerating(false));
  };

  if (!rows) return <Spin />;

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} loading={generating} onClick={handleGenerate}>
          Generate Engagement Letter
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: 'Number', dataIndex: 'letter_number', render: (v, l) => <Link to={`/engagement-letters/${l.id}`}>{v}</Link> },
          { title: 'Issued', dataIndex: 'issue_date', render: (v) => fmtDate(v) },
          { title: 'Entity', key: 'entity', render: (_, l) => l.Entity?.entity_name || '-' },
          { title: 'Services', key: 'services', render: (_, l) => (l.EngagementLetterServices || []).map((s) => s.description).join(', ') },
          { title: 'Signed', dataIndex: 'signed_at', render: (v, l) => (v ? `${fmtDate(v)} - ${l.signed_by_name}` : '-') },
          { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={LETTER_STATUS_COLORS[v]}>{label(v)}</Tag> },
        ]}
      />
    </div>
  );
}

const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Statement of Account (spec §28): issued invoices + receipts with a running balance, from /invoices/statement. */
function StatementTab({ clientId }) {
  const [statement, setStatement] = React.useState(null);

  React.useEffect(() => {
    request
      .get('/invoices/statement', { client_id: clientId })
      .then((r) => r.json())
      .then((r) => r.status && setStatement(r.data));
  }, [clientId]);

  if (!statement) return <Spin />;

  return (
    <div>
      <Space size="large" style={{ marginBottom: 16 }}>
        <Typography.Text>Invoiced: <strong>{money(statement.totals.invoiced)}</strong></Typography.Text>
        <Typography.Text>Paid: <strong>{money(statement.totals.paid)}</strong></Typography.Text>
        {Number(statement.totals.credited) > 0 && (
          <Typography.Text>Credited: <strong>{money(statement.totals.credited)}</strong></Typography.Text>
        )}
        <Typography.Text type={statement.totals.outstanding > 0 ? 'danger' : 'success'}>
          Outstanding: <strong>{money(statement.totals.outstanding)}</strong>
        </Typography.Text>
        <Link to={`/invoices?client_id=${clientId}`}>All invoices</Link>
      </Space>
      <Table
        rowKey={(e) => `${e.type}-${e.reference}`}
        size="small"
        pagination={false}
        dataSource={statement.entries}
        columns={[
          { title: 'Date', dataIndex: 'date', render: (v) => fmtDate(v) },
          { title: 'Type', dataIndex: 'type', render: (v) => <Tag color={{ INVOICE: 'blue', PAYMENT: 'green', CREDIT_NOTE: 'orange' }[v]}>{label(v)}</Tag> },
          { title: 'Reference', key: 'ref', render: (_, e) => (e.type === 'INVOICE' ? <Link to={`/invoices/${e.invoice_id}`}>{e.reference}</Link> : e.reference) },
          { title: 'Description', dataIndex: 'description' },
          { title: 'Debit', dataIndex: 'debit', align: 'right', render: (v) => (v ? money(v) : '') },
          { title: 'Credit', dataIndex: 'credit', align: 'right', render: (v) => (v ? money(v) : '') },
          { title: 'Balance', dataIndex: 'balance', align: 'right', render: (v) => <strong>{money(v)}</strong> },
        ]}
      />
    </div>
  );
}

/** The client's proposals (numbered PRP-YYYY-0001), created through the shared proposal editor. */
function ClientProposalsTab({ clientId, onChanged }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const fetchRows = React.useCallback(() => {
    setLoading(true);
    request
      .get('/proposals', { client_id: clientId })
      .then((r) => r.json())
      .then((r) => r.status && setRows(r.data))
      .finally(() => setLoading(false));
  }, [clientId]);
  React.useEffect(fetchRows, [fetchRows]);

  const changed = () => {
    fetchRows();
    onChanged();
  };

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          New Proposal
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        loading={loading}
        dataSource={rows}
        expandable={{ expandedRowRender: (p) => <div style={{ whiteSpace: 'pre-wrap' }}>{p.details || 'No proposal details recorded.'}</div> }}
        columns={proposalColumns({
          onEdit: (p) => {
            setEditing(p);
            setEditorOpen(true);
          },
          onChange: changed,
        })}
      />
      <ProposalEditorModal open={editorOpen} onClose={() => setEditorOpen(false)} onSaved={changed} target={{ client_id: clientId }} proposal={editing} />
    </div>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const [client, setClient] = React.useState(null);
  const [form] = Form.useForm();

  const fetchClient = () => {
    request
      .get(`/clients/${id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          setClient(r.data);
          form.setFieldsValue({
            ...r.data,
            risk_classification_date: r.data.risk_classification_date ? moment(r.data.risk_classification_date) : null,
            last_kyc_review_date: r.data.last_kyc_review_date ? moment(r.data.last_kyc_review_date) : null,
            next_kyc_review_date: r.data.next_kyc_review_date ? moment(r.data.next_kyc_review_date) : null,
          });
        }
      })
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchClient, [id]);

  const handleSaveDetails = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        company_id: client.company_id,
        risk_classification_date: values.risk_classification_date ? values.risk_classification_date.format('YYYY-MM-DD') : null,
        last_kyc_review_date: values.last_kyc_review_date ? values.last_kyc_review_date.format('YYYY-MM-DD') : null,
        next_kyc_review_date: values.next_kyc_review_date ? values.next_kyc_review_date.format('YYYY-MM-DD') : null,
      };
      request
        .put(`/clients/${id}`, payload)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Client updated' });
            fetchClient();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  // Only the first load blanks the page; a refetch after a save keeps the tabs (and any open modal) mounted.
  if (!client) return <Spin style={{ marginTop: 40 }} />;

  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 0 }}>
        {client.client_name} {client.client_code && <Tag>{client.client_code}</Tag>}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {(client.Entities || []).length} legal {(client.Entities || []).length === 1 ? 'entity' : 'entities'}
        {(client.Entities || []).find((e) => e.is_primary) ? ` - primary: ${(client.Entities || []).find((e) => e.is_primary).entity_name}` : ''}
      </Typography.Paragraph>

      <Tabs
        items={[
          {
            key: 'details',
            label: 'Profile / KYC',
            children: (
              <Form form={form} layout="vertical" style={{ maxWidth: 600 }}>
                <Typography.Paragraph type="secondary">
                  The client relationship. Registration, tax and SI details belong to the client's legal entities - see the Entities tab.
                </Typography.Paragraph>
                <Form.Item name="client_name" label="Client Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="client_code" label="Client Code" tooltip="Your firm's own reference, unique per company">
                  <Input placeholder="e.g. CL-0001" />
                </Form.Item>
                <Form.Item name="client_type" label="Type">
                  <Select options={CLIENT_TYPES.map((t) => ({ value: t, label: t }))} />
                </Form.Item>
                <Form.Item name="country_of_origin" label="Country of Origin">
                  <CountrySelect />
                </Form.Item>
                <Form.Item name="principal_activities" label="Principal Activities">
                  <Input.TextArea rows={2} />
                </Form.Item>

                <Typography.Title level={5}>Contact</Typography.Title>
                <Form.Item name="contact_person" label="Contact Person">
                  <Input />
                </Form.Item>
                <Form.Item name="contact_email" label="Email" rules={[{ type: 'email' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="contact_phone" label="Phone">
                  <Input />
                </Form.Item>
                <Form.Item name="notes" label="Notes">
                  <Input.TextArea rows={3} placeholder="Internal notes for colleagues continuing work on this client" />
                </Form.Item>

                <Typography.Title level={5}>KYC / Risk</Typography.Title>
                <Form.Item name="risk_level" label="Risk Level">
                  <Select allowClear options={RISK_LEVELS.map((r) => ({ value: r, label: r }))} />
                </Form.Item>
                <Form.Item name="risk_classification_date" label="Risk Classification Date">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="last_kyc_review_date" label="Last KYC Review">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="next_kyc_review_date" label="Next KYC Review">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>

                <Button type="primary" onClick={handleSaveDetails}>
                  Save
                </Button>
              </Form>
            ),
          },
          {
            key: 'entities',
            label: `Entities (${(client.Entities || []).length})`,
            children: <EntitiesTab clientId={id} entities={client.Entities || []} onSaved={fetchClient} />,
          },
          {
            key: 'officers',
            label: `Officers (${(client.ClientOfficers || []).length})`,
            children: (
              <NestedResourceTab
                clientId={id}
                endpoint="officers"
                entities={client.Entities || []}
                rows={client.ClientOfficers || []}
                onSaved={fetchClient}
                columns={[
                  { title: 'Type', dataIndex: 'officer_type' },
                  { title: 'Kind', dataIndex: 'officer_kind' },
                  { title: 'Name', dataIndex: 'name' },
                  { title: 'Nationality', dataIndex: 'nationality' },
                  { title: 'Shareholding %', dataIndex: 'shareholding_percent' },
                ]}
                fields={
                  <>
                    <Form.Item name="officer_type" label="Type" rules={[{ required: true }]}>
                      <Select options={['SHAREHOLDER', 'DIRECTOR', 'SECRETARY'].map((v) => ({ value: v, label: v }))} />
                    </Form.Item>
                    <Form.Item name="officer_kind" label="Kind" initialValue="INDIVIDUAL">
                      <Select options={['INDIVIDUAL', 'CORPORATE'].map((v) => ({ value: v, label: v }))} />
                    </Form.Item>
                    <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="nationality" label="Nationality">
                      <CountrySelect />
                    </Form.Item>
                    <Form.Item name="shareholding_percent" label="Shareholding %">
                      <InputNumber style={{ width: '100%' }} min={0} max={100} />
                    </Form.Item>
                  </>
                }
              />
            ),
          },
          {
            key: 'ubos',
            label: `UBOs (${(client.ClientUBOs || []).length})`,
            children: (
              <NestedResourceTab
                clientId={id}
                endpoint="ubos"
                entities={client.Entities || []}
                rows={client.ClientUBOs || []}
                onSaved={fetchClient}
                columns={[
                  { title: 'Name', dataIndex: 'name' },
                  { title: 'Nationality', dataIndex: 'nationality' },
                  { title: 'Ownership %', dataIndex: 'ownership_percent' },
                  { title: 'Control', dataIndex: 'control_type' },
                ]}
                fields={
                  <>
                    <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="nationality" label="Nationality">
                      <CountrySelect />
                    </Form.Item>
                    <Form.Item name="ownership_percent" label="Ownership %">
                      <InputNumber style={{ width: '100%' }} min={0} max={100} />
                    </Form.Item>
                    <Form.Item name="control_type" label="Control Type">
                      <Select allowClear options={['DIRECT', 'INDIRECT'].map((v) => ({ value: v, label: v }))} />
                    </Form.Item>
                  </>
                }
              />
            ),
          },
          {
            key: 'kyc-documents',
            label: `KYC Documents (${(client.ClientKYCDocuments || []).length})`,
            children: (
              <NestedResourceTab
                clientId={id}
                endpoint="kyc-documents"
                attachmentsOwnerType="KYC_DOCUMENT"
                entities={client.Entities || []}
                rows={client.ClientKYCDocuments || []}
                onSaved={fetchClient}
                columns={[
                  { title: 'Type', dataIndex: 'document_type' },
                  { title: 'Number', dataIndex: 'document_number' },
                  { title: 'Expiry', dataIndex: 'expiry_date', render: (v) => fmtDate(v) },
                ]}
                fields={
                  <>
                    <Form.Item name="document_type" label="Document Type" rules={[{ required: true }]}>
                      <Select
                        options={[
                          'PASSPORT_ID',
                          'PROOF_OF_ADDRESS',
                          'CORPORATE_REGISTRATION_CERTIFICATE',
                          'SHAREHOLDER_REGISTER',
                          'OTHER',
                        ].map((v) => ({ value: v, label: v }))}
                      />
                    </Form.Item>
                    <Form.Item name="document_number" label="Document Number">
                      <Input />
                    </Form.Item>
                    <Form.Item name="expiry_date" label="Expiry Date">
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                }
              />
            ),
          },
          {
            key: 'document-requests',
            label: `Document Requests (${(client.ClientDocumentRequests || []).filter((r) => r.status === 'PENDING').length} pending)`,
            children: (
              <NestedResourceTab
                clientId={id}
                endpoint="document-requests"
                attachmentsOwnerType="DOCUMENT_REQUEST"
                entities={client.Entities || []}
                rows={client.ClientDocumentRequests || []}
                onSaved={fetchClient}
                columns={[
                  { title: 'Requested', dataIndex: 'category', render: (v) => v.replace(/_/g, ' ') },
                  { title: 'Due', dataIndex: 'due_date', render: (v) => fmtDate(v) },
                  { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={v === 'FULFILLED' ? 'green' : 'orange'}>{label(v)}</Tag> },
                  { title: 'Fulfilled', dataIndex: 'fulfilled_at', render: (v) => (v ? moment(v).format('YYYY-MM-DD') : '-') },
                ]}
                fields={
                  <>
                    <Typography.Paragraph type="secondary">
                      Ask the client for a document. Uploading a file against the request marks it fulfilled.
                    </Typography.Paragraph>
                    <Form.Item name="category" label="Document requested" rules={[{ required: true }]}>
                      <Select
                        options={['ACCOUNTING_RECORDS', 'BANK_STATEMENTS', 'IDENTIFICATION_DOCUMENT', 'CORPORATE_CERTIFICATE', 'OTHER'].map((v) => ({
                          value: v,
                          label: v.replace(/_/g, ' '),
                        }))}
                      />
                    </Form.Item>
                    <Form.Item name="due_date" label="Due Date">
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                }
              />
            ),
          },
          {
            key: 'proposals',
            label: `Proposals (${(client.ClientProposals || []).length})`,
            children: (
              <ClientProposalsTab clientId={+id} onChanged={fetchClient} />
            ),
          },
          {
            key: 'letters',
            label: 'Engagement Letters',
            children: <LettersTab clientId={id} entities={client.Entities || []} />,
          },
          {
            key: 'engagements',
            label: `Service Engagements (${(client.ClientServiceEngagements || []).length})`,
            children: (
              <EngagementsTab clientId={id} rows={client.ClientServiceEngagements || []} onSaved={fetchClient} entities={client.Entities || []} />
            ),
          },
          {
            key: 'deadlines',
            label: `Deadlines (${(client.Deadlines || []).length})`,
            children: <DeadlinesTab clientId={id} rows={client.Deadlines || []} onSaved={fetchClient} />,
          },
          {
            key: 'files',
            label: 'Files',
            children: (
              <div>
                <Typography.Paragraph type="secondary">
                  General files for this client. Files that belong to a specific KYC document, document request or engagement letter are attached
                  on that record instead.
                </Typography.Paragraph>
                <AttachmentsPanel ownerType="CLIENT" ownerId={+id} categories={['GENERAL', 'KYC', 'CORRESPONDENCE', 'SUPPORTING_DOCUMENT']} />
              </div>
            ),
          },
          {
            key: 'workbook',
            label: 'Workbook status',
            children: <ClientWorkbookTab clientId={id} />,
          },
          {
            key: 'books',
            label: 'Accounting / Books',
            children: <ClientBooksCard client={client} onChanged={fetchClient} />,
          },
          {
            key: 'statement',
            label: 'Statement of Account',
            children: <StatementTab clientId={id} />,
          },
        ]}
      />
    </div>
  );
}
